import { FormEvent, useState, ChangeEvent, useEffect } from "react";
import { useAppDispatch } from "../../redux/hooks";
import toast from "react-hot-toast";
import { login } from "../../redux/thunk";
import LoadingSpinner from "../../components/elements/LoadingSpinner";
import { Link, useNavigate } from "react-router-dom";
import { GoEyeClosed, GoEye } from "react-icons/go";

interface Errors {
  email?: string;
  password?: string;
}

const Login = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const [errors, setErrors] = useState<Errors>({});
  const [inputValue, setInputValue] = useState({
    email: "",
    password: "",
  });
  const [showPassword, setShowPassword] = useState(false);
  const [isPageLoading, setIsPageLoading] = useState(true); // For initial page load
  const [isSubmitting, setIsSubmitting] = useState(false); // For form submission
  const [rememberMe, setRememberMe] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => {
      setIsPageLoading(false);
    }, 2000);
    return () => clearTimeout(timer);
  }, []);

  const togglePasswordVisibility = () => {
    setShowPassword((prevShowPassword) => !prevShowPassword);
  };

  const handleInputChange = (e: ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    if (value.trim() !== "") {
      setErrors({
        ...errors,
        [name]: undefined,
      });
    }
    setInputValue((prevState) => ({
      ...prevState,
      [name]: value,
    }));
  };

  const validateForm = () => {
    let hasErrors = false;
    const newErrors: Errors = {};

    if (inputValue.email.trim() === "") {
      newErrors.email = "Please enter your valid email";
      hasErrors = true;
    }

    const passwordRegex = /^(?=.*[a-zA-Z])(?=.*\d).{8,}$/;
    if (!passwordRegex.test(inputValue.password)) {
      newErrors.password =
        "please use the correct password";
      hasErrors = true;
    }

    setErrors(newErrors);
    return hasErrors;
  };

  const handleLogin = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (validateForm()) {
      return;
    }
    setIsSubmitting(true);
    const loginData = {
      email: inputValue.email.toLowerCase(),
      password: inputValue.password,
    };
    try {
      await dispatch(login(loginData)).then((res) => {
        if (res.type === "login/fulfilled") {
          toast.success("Login successful");
          window.location.href = "/dashboard";
          setIsSubmitting(false);
        }
      });
    } catch (error) {
      console.error("Login error:", error);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="flex items-center justify-center min-h-screen bg-[#F3F4F6] font-lato p-4 md:p-6">
      <div className={`w-full max-w-[450px] md:max-w-5xl bg-white rounded-3xl shadow-xl flex flex-col md:flex-row overflow-hidden transition-opacity duration-1000 ease-in-out ${isPageLoading ? "opacity-0" : "opacity-100"}`}>

        {/* Left Side - Logo container (Hidden on mobile) */}
        <div className="hidden md:flex md:w-1/2 md:flex-shrink-0 md:basis-1/2 bg-green-50/50 p-12 items-center justify-center min-h-[400px]">
          <img
            src="/assets/logo-foodbank.png"
            alt="Way Gibb Logo"
            className="w-auto h-20 md:h-28 max-w-[80%] object-contain drop-shadow-sm"
          />
        </div>

        {/* Right Side - Form */}
        <div className="w-full md:w-1/2 md:flex-shrink-0 md:basis-1/2 p-8 md:p-10 lg:p-14 bg-white flex flex-col justify-center">
          <div className="flex md:hidden justify-center mb-8">
            <img
              src="/assets/logo-foodbank.png"
              alt="Way Gibb Logo"
              className="w-auto h-16 object-contain"
            />
          </div>

          <h1 className="text-2xl md:text-3xl text-gray-800 font-bold mb-8 text-center md:text-left">
            Welcome Back
          </h1>

          <form className="w-full" onSubmit={handleLogin}>

            {/* Username or Email */}
            <div className="flex flex-col mb-5">
              <label className="text-sm text-gray-700 font-medium mb-1.5">
                Email
              </label>
              <input
                name="email"
                value={inputValue.email}
                onChange={handleInputChange}
                type="text"
                placeholder="e.g. samuel@sovereigntechltd.com"
                className={`w-full px-4 py-3 border rounded-xl outline-none transition-colors text-sm ${errors.email ? "border-red-500 focus:border-red-500" : "border-gray-200 focus:border-secondary"
                  }`}
              />
              {errors.email && (
                <p className="text-red-500 text-xs mt-1">{errors.email}</p>
              )}
            </div>
            <div className="flex flex-col mb-5 relative">
              <label className="text-sm text-gray-700 font-medium mb-1.5">
                Password
              </label>
              <div className="relative">
                <input
                  name="password"
                  value={inputValue.password}
                  onChange={handleInputChange}
                  type={showPassword ? "text" : "password"}
                  placeholder="Enter your password"
                  className={`w-full px-4 py-3 pr-12 border rounded-xl outline-none transition-colors text-sm ${errors.password ? "border-red-500 focus:border-red-500" : "border-gray-200 focus:border-secondary"
                    }`}
                />
                <button
                  type="button"
                  onClick={togglePasswordVisibility}
                  className="absolute right-4 top-1/2 -translate-y-1/2 text-xl text-gray-500 hover:text-secondary transition-colors"
                >
                  {showPassword ? <GoEye /> : <GoEyeClosed />}
                </button>
              </div>
              {errors.password && (
                <p className="text-red-500 text-xs mt-1">{errors.password}</p>
              )}
            </div>
            <div className="flex items-center mb-8">
              <label className="flex items-center cursor-pointer group">
                <input
                  type="checkbox"
                  checked={rememberMe}
                  onChange={() => setRememberMe(!rememberMe)}
                  className="w-4 h-4 text-secondary bg-white border-gray-300 rounded focus:ring-secondary focus:ring-2 cursor-pointer"
                />
                <span className="ml-2 text-sm text-gray-600 select-none group-hover:text-gray-800 transition-colors">
                  Keep me logged in
                </span>
              </label>
            </div>

            <button
              className="w-full bg-secondary text-white font-semibold py-3.5 rounded-2xl shadow-lg shadow-green-100 hover:bg-green-700 transition-all duration-300 mb-6 flex justify-center items-center"
              type="submit"
              disabled={isSubmitting}
            >
              {isSubmitting ? <LoadingSpinner /> : "Login"}
            </button>

          </form>
        </div>
      </div>

      {isPageLoading && (
        <div className="fixed inset-0 bg-white/90 z-50 flex items-center justify-center">
          <div className="animate-pulse flex flex-col items-center">
            <img
              src="/assets/sidebar-logo.svg"
              alt="Loading..."
              className="w-16 h-16"
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default Login;
